import { User as AuthUser } from 'firebase/auth';
import {
  computed,
  Inject,
  inject,
  Injectable,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Credential, Env, Flavor, SafeStorage, Team, User } from '@uploadcam/common';
import { environment } from '../../../../environments/environment';
import {
  TEAM_GET_CURRENT$,
  USER_AUTH_STATE_CHANGED$,
  USER_GET_CURRENT$,
} from '../../../core/core.module';
import { GetCredentialInteractor } from '../../../../features/auth/domain/interactors/get-credential.interactor';
import { GetTeamByIdInteractor } from '../../../../features/team/domain/interactors/get-team-by-id.interactor';
import { DialogService, MessageType } from './dialog.service';
import {
  UpgradeScopesDialogComponent,
  UpgradeScopesDialogInput,
  UpgradeScopesDialogResult,
} from '../components/upgrade-scopes-dialog/upgrade-scopes-dialog.component';
import invariant from 'tiny-invariant';
import { getLogger } from '../../../core/utils';

const EMBEDDED_FLAG_KEY = 'uploadcam_embedded';

export interface FeatureContext {
  flavor: Flavor;
  env: Env;
  isEmbedded: boolean;
  credential?: Credential;
  user?: User;
  team?: Team;
}

type FeatureComputeFn<T extends FeatureContext = FeatureContext> = (ctx: T) => boolean | string[];

interface FeatureResultBase {
  runGuarded(actionFn: () => any): void;
}

interface FeatureResultYes extends FeatureResultBase {
  result: 'yes';
}

interface FeatureResultNo extends FeatureResultBase {
  result: 'no';
}

interface FeatureResultAuthorize extends FeatureResultBase {
  result: 'authorize';
  extraScopes: string[];
}

type FeatureResult = FeatureResultYes | FeatureResultNo | FeatureResultAuthorize;

/**
 * `FeatureResult` signal but wrapped in a proxy to hide it's a signal. You can
 * access properties directly without calling the signal.
 */
type FeatureResultProxy = FeatureResult & {};

@Injectable({ providedIn: 'root' })
export class FeaturesService {
  private readonly logger = getLogger('FeaturesService');
  private readonly storage = new SafeStorage(sessionStorage);

  private lastTeamId?: string;
  readonly #context: WritableSignal<FeatureContext> = signal({
    flavor: environment.flavor,
    env: environment.env,
    isEmbedded: !!this.storage.getItem(EMBEDDED_FLAG_KEY),
  });

  constructor(
    private readonly dialog: DialogService,
    private readonly getCredential: GetCredentialInteractor,
    private readonly getTeamById: GetTeamByIdInteractor,
    @Inject(TEAM_GET_CURRENT$)
    private readonly teamGetCurrent$: Subject<Team>,
    @Inject(USER_AUTH_STATE_CHANGED$)
    private readonly userAuthStateChanged$: Observable<AuthUser | undefined>,
    @Inject(USER_GET_CURRENT$)
    private readonly userGetCurrent$: Observable<User>,
  ) {
    this.userAuthStateChanged$.subscribe(async (user) => {
      if (!user) {
        // Logout
        this.#context.update((ctx) => ({
          ...ctx,
          credential: undefined,
          user: undefined,
          team: undefined,
        }));
      }
    });

    this.userGetCurrent$.subscribe(async (user) => {
      const credential = this.getCredential.execute();

      if (user.teamId && user.teamId !== this.lastTeamId) {
        this.lastTeamId = user.teamId;
        this.getTeamById
          .execute(user.teamId)
          .then((team) => {
            this.#context.update((ctx) => ({
              ...ctx,
              team,
            }));
          })
          .catch(() => {
            this.lastTeamId = undefined;
          });
      }

      this.#context.update((ctx) => ({
        ...ctx,
        credential,
        user,
      }));
    });

    this.teamGetCurrent$.subscribe((team) => {
      this.#context.update((ctx) => ({
        ...ctx,
        team,
      }));
    });
  }

  public get context(): Signal<FeatureContext> {
    return this.#context.asReadonly();
  }

  protected readonly openUpgradeScopes = this.dialog.createOpenDialogFn<
    UpgradeScopesDialogInput,
    UpgradeScopesDialogResult
  >(UpgradeScopesDialogComponent);

  public async upgradeScopes(
    credential: Credential,
    scopes: string[],
    actionFn: () => void,
  ): Promise<void> {
    this.logger.info(`Start scopes upgrade for "${credential.provider}": ${scopes.join(', ')}`);

    const newCredential = await this.openUpgradeScopes({ credential, scopes });

    if (newCredential) {
      this.#context.update((ctx) => ({
        ...ctx,
        credential: newCredential,
      }));

      actionFn();
    } else {
      this.dialog.snack('upgradeScopesDialog.cta', MessageType.Info);
    }
  }

  public setEmbedded(isEmbedded: boolean): void {
    this.#context.update((ctx) => ({
      ...ctx,
      isEmbedded,
    }));

    if (isEmbedded) {
      this.storage.setItem(EMBEDDED_FLAG_KEY, '1');
    } else {
      this.storage.removeItem(EMBEDDED_FLAG_KEY);
    }
  }

  public computed<T extends object>(
    ctxFn: () => T,
    evaluateFn: FeatureComputeFn<FeatureContext & T>,
  ): FeatureResultProxy {
    const resultSignal = computed<FeatureResult>(() => {
      const context = this.context();
      const result = evaluateFn({ ...context, ...ctxFn() });

      if (result === true) {
        return {
          result: 'yes',
          runGuarded: (actionFn) => actionFn(),
        };
      } else if (result === false) {
        return { result: 'no', runGuarded: () => {} };
      } else if (Array.isArray(result)) {
        const extraScopes = result;

        return {
          result: 'authorize',
          extraScopes,
          runGuarded: (actionFn: () => void) => {
            invariant(context.credential, 'Credential is required to upgrade scopes');
            void this.upgradeScopes(context.credential, extraScopes, actionFn);
          },
        };
      }

      throw new Error('Invalid feature evaluation result');
    });

    // Wrap the result signal in a proxy to allow accessing properties directly
    // which allows type narrowing in Angular templates.
    return new Proxy(resultSignal, {
      get(target, prop) {
        return (target() as any)[prop];
      },
    }) as unknown as FeatureResultProxy;
  }

  public computeFeatureResult(evaluateFn: FeatureComputeFn): FeatureResultProxy {
    const resultSignal = computed<FeatureResult>(() => {
      const context = this.context();
      const result = evaluateFn(context);

      if (result === true) {
        return {
          result: 'yes',
          runGuarded: (actionFn) => actionFn(),
        };
      } else if (result === false) {
        return { result: 'no', runGuarded: () => {} };
      } else if (Array.isArray(result)) {
        const extraScopes = result;

        return {
          result: 'authorize',
          extraScopes,
          runGuarded: (actionFn: () => void) => {
            invariant(context.credential, 'Credential is required to upgrade scopes');
            void this.upgradeScopes(context.credential, extraScopes, actionFn);
          },
        };
      }

      throw new Error('Invalid feature evaluation result');
    });

    // Wrap the result signal in a proxy to allow accessing properties directly
    // which allows type narrowing in Angular templates.
    return new Proxy(resultSignal, {
      get(target, prop) {
        return (target() as any)[prop];
      },
    }) as unknown as FeatureResultProxy;
  }
}

export function defineFeature(evaluateFn: FeatureComputeFn): FeatureResultProxy {
  return inject(FeaturesService).computed(() => ({}), evaluateFn);
}
